import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useCampaignVersions, usePublish, useRollback, type Campaign } from '@/lib/queries';
import { toast } from 'sonner';

export function PublishPanel({
  campaign,
  appId,
}: {
  campaign: Campaign;
  appId: string | null;
}): React.JSX.Element {
  const { data: history, isLoading } = useCampaignVersions(campaign.id);
  const publish = usePublish(campaign.id, appId);
  const rollback = useRollback(campaign.id, appId);

  const blocked = !campaign.segment_id || !campaign.experience_id;
  const blockedReason = 'Link a segment and an experience first — the API rejects a publish without both.';

  function onPublish(): void {
    publish.mutate(undefined, {
      onSuccess: (result) =>
        // Publishing recompiles the ENVIRONMENT, so the count is the honest thing to report: the
        // author pressed a button on one campaign and shipped a bundle holding every live one.
        toast.success(
          `Published version ${result.published_bundle_version} — ${result.campaigns} campaign${result.campaigns === 1 ? '' : 's'} live.`
        ),
      onError: (error: Error) => toast.error(error.message),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Publish</CardTitle>
        <CardDescription>
          Recompiles every live campaign in this environment into one immutable, versioned bundle —
          and activates this one.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* A disabled button swallows pointer events, so the tooltip needs a live wrapper. */}
              <span className="inline-block">
                <Button onClick={onPublish} disabled={blocked || publish.isPending}>
                  {publish.isPending ? 'Publishing…' : 'Publish'}
                </Button>
              </span>
            </TooltipTrigger>
            {blocked && <TooltipContent>{blockedReason}</TooltipContent>}
          </Tooltip>
        </TooltipProvider>

        <Separator />

        <div className="space-y-2">
          <h3 className="text-sm font-medium">Version history</h3>
          {isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : !history || history.versions.length === 0 ? (
            <p className="text-sm text-muted-foreground">Never published to this environment.</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {[...history.versions]
                .sort((a, b) => b - a)
                .map((version) => {
                  const current = version === history.current;
                  return (
                    <li key={version} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span className="tabular-nums">
                        Version {version}
                        {current && (
                          <span className="ml-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                            current
                          </span>
                        )}
                      </span>
                      {!current && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm" disabled={rollback.isPending}>
                              Roll back
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Roll back to version {version}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Repoints instantly — no rebuild. Devices pick up version {version} on their
                                next bundle fetch.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() =>
                                  rollback.mutate(version, {
                                    onSuccess: () => toast.success(`Rolled back to version ${version}.`),
                                    onError: (error: Error) => toast.error(error.message),
                                  })
                                }
                              >
                                Roll back
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
